import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Badge, Card, Container, Divider, Overline, Skeleton } from '@/design-system/primitives'
import { useAdminContext } from '@/features/admin/session'
import { formatMoney } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'

import {
  useDownloadExport,
  useExportCatalogue,
  useOverview,
  useRevenue,
  useTimeline,
} from '../api'
import type { FunnelStep } from '../api'

/**
 * Bilan de l'édition.
 *
 * Tous les chiffres sont recalculés côté serveur à chaque ouverture : rien
 * n'est stocké, donc rien ne peut dériver de la réalité qu'il décrit.
 *
 * Le bloc « argent » demande un droit distinct des volumes. S'il manque, la
 * requête répond 403 et la section disparaît — ce n'est pas une panne, c'est
 * une réponse.
 */
export function AnalyticsPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('analytics.overline')}>
      {(_session, signOut) => <AnalyticsBoard onSignOut={signOut} />}
    </StaffGate>
  )
}

function AnalyticsBoard({ onSignOut }: { onSignOut: () => void }) {
  const { t, i18n } = useTranslation()
  // Même résolution que le back-office : le bilan se lit surtout APRÈS la
  // soirée, quand l'édition est close et que l'API publique ne la montre plus.
  const { event, eventId } = useAdminContext()

  const overview = useOverview(eventId, true)
  const revenue = useRevenue(eventId, true)
  const timeline = useTimeline(eventId, true)
  const catalogue = useExportCatalogue(eventId, true)
  const download = useDownloadExport(eventId)

  if (isForbidden(overview.error)) {
    return <NoAccess title={t('analytics.overline')} onSignOut={onSignOut} />
  }

  if (overview.isPending) {
    return (
      <Container className="py-12">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-8 h-64 w-full" />
      </Container>
    )
  }

  if (overview.isError || !overview.data) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-md text-content-primary">
          {t('analytics.unavailable')}
        </h1>
        <p className="mt-4 text-content-secondary">{t('live.unavailableHelp')}</p>
      </Container>
    )
  }

  const data = overview.data
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: data.currency }, i18n.language)

  return (
    <Container className="py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Overline>{t('analytics.overline')}</Overline>
        <LanguageSwitcher />
      </div>
      <h1 className="mt-2 font-display text-heading-lg text-content-primary">
        {event?.name ?? data.event_name}
      </h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={t('analytics.ordersPaid')} value={data.orders_paid} highlight />
        <Stat label={t('analytics.attendees')} value={data.attendees_expected} />
        <Stat label={t('checkin.checkedIn')} value={data.attendees_checked_in} />
        <Stat label={t('analytics.pending')} value={data.orders_pending} alert={data.orders_pending > 0} />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={t('analytics.food')} value={data.food_items_sold} />
        <Stat label={t('live.tables')} value={data.tables_reserved} />
        <Stat label={t('live.voting')} value={data.votes_cast} />
        <Stat label={t('analytics.candidates')} value={data.candidates_approved} />
      </div>

      <section className="mt-12">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('analytics.funnel')}
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-content-secondary">
          {t('analytics.funnelHelp')}
        </p>
        <ul className="mt-6 space-y-3">
          {data.funnel.map((step) => (
            <FunnelRow key={step.key} step={step} max={data.funnel[0]?.count ?? 1} />
          ))}
        </ul>
      </section>

      {/* Argent : présent uniquement si le droit l'autorise. */}
      {revenue.data ? (
        <>
          <Divider className="my-12" />
          <h2 className="font-display text-heading-md text-content-primary">
            {t('analytics.revenue')}
          </h2>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label={t('analytics.net')} value={money(revenue.data.net_minor)} highlight />
            <Stat label={t('analytics.collected')} value={money(revenue.data.collected_minor)} />
            <Stat label={t('analytics.refunded')} value={money(revenue.data.refunded_minor)} />
            <Stat
              label={t('analytics.outstanding')}
              value={money(revenue.data.outstanding_minor)}
              alert={revenue.data.outstanding_minor > 0}
            />
          </div>

          <p className="mt-3 text-sm text-content-muted">
            {t('analytics.averageOrder', { value: money(revenue.data.average_order_minor) })}
          </p>

          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <Card>
              <p className="overline text-content-muted">{t('analytics.byType')}</p>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={revenue.data.by_type.map((row) => ({
                      name: t(`analytics.type.${row.item_type}`, row.item_type),
                      value: row.net_minor / 100,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line-subtle)" />
                    <XAxis dataKey="name" stroke="var(--content-muted)" fontSize={12} />
                    <YAxis stroke="var(--content-muted)" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--line)',
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="value" fill="var(--ember-500)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {timeline.data ? (
              <Card>
                <p className="overline text-content-muted">{t('analytics.perDay')}</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={timeline.data.points.map((point) => ({
                        name: point.day,
                        value: point.net_minor / 100,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--line-subtle)" />
                      <XAxis dataKey="name" stroke="var(--content-muted)" fontSize={12} />
                      <YAxis stroke="var(--content-muted)" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--line)',
                          borderRadius: 8,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--champagne-400)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            ) : null}
          </div>

          {timeline.data && timeline.data.top_items.length > 0 ? (
            <section className="mt-10">
              <p className="overline text-content-muted">{t('analytics.topItems')}</p>
              <ul className="mt-4 space-y-2">
                {timeline.data.top_items.map((item) => (
                  <li
                    key={`${item.item_type}-${item.ref_id ?? item.label}`}
                    className="flex items-baseline justify-between gap-6 border-b border-line-subtle pb-2 text-sm"
                  >
                    <span className="text-content-primary">{item.label}</span>
                    <span className="font-mono tabular-nums text-content-muted">
                      {item.quantity} · {money(item.net_minor)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}

      {catalogue.data ? (
        <>
          <Divider className="my-12" />
          <h2 className="font-display text-heading-md text-content-primary">
            {t('analytics.exports')}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-content-secondary">
            {t('analytics.exportsHelp')}
          </p>

          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {catalogue.data.exports.map((kind) => (
              <li key={kind.key}>
                <Card className="flex h-full flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-heading-sm text-content-primary">
                      {kind.label}
                    </h3>
                    {/* Dit avant le clic ce que le fichier contient. */}
                    {kind.personal_data ? (
                      <Badge tone="ember">{t('analytics.personalData')}</Badge>
                    ) : null}
                  </div>
                  <p className="text-sm text-content-secondary">{kind.description}</p>
                  <Button
                    className="mt-auto self-start"
                    variant="outline"
                    size="sm"
                    disabled={download.isPending}
                    onClick={() => download.mutate({ key: kind.key })}
                  >
                    {t('analytics.download')}
                  </Button>
                  {/* L'échec d'un export ne doit pas ressembler à un clic ignoré. */}
                  {download.isError && download.variables?.key === kind.key ? (
                    <p className="text-sm text-state-danger">{t('admin.closing.downloadFailed')}</p>
                  ) : null}
                </Card>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Container>
  )
}

function Stat({
  label,
  value,
  highlight = false,
  alert = false,
}: {
  label: string
  value: string | number
  highlight?: boolean
  alert?: boolean
}) {
  return (
    <Card className="p-5">
      <p className="overline text-content-muted">{label}</p>
      <p
        className={`mt-2 font-display text-heading-lg tabular-nums ${
          highlight ? 'text-champagne-300' : alert ? 'text-ember-300' : 'text-content-primary'
        }`}
      >
        {value}
      </p>
    </Card>
  )
}

function FunnelRow({ step, max }: { step: FunnelStep; max: number }) {
  const { t } = useTranslation()
  const width = max > 0 ? (step.count / max) * 100 : 0

  return (
    <li>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="text-content-primary">{t(`analytics.step.${step.key}`, step.key)}</span>
        <span className="font-mono tabular-nums text-content-muted">
          {step.count}
          {step.conversion !== null ? ` · ${step.conversion}%` : ''}
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-bg-overlay">
        <div className="h-full rounded-full bg-ember" style={{ width: `${width}%` }} />
      </div>
    </li>
  )
}
